import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Card, Headline, Text, JsonData } from "../common";
import * as S from "./CarrousselCard.Style";
import { applyKey } from "../../utils";

const CarrousselAction = ({ itemIdx, activeIndex, onActionClick }) => {
  const onClickHandle = useCallback(
    (idx) => {
      if (onActionClick) {
        onActionClick(idx);
      }
    },
    [onActionClick]
  );

  return (
    <S.CarrousselControl.Action
      className={`CarrousselControl Item${
        activeIndex === itemIdx ? " active" : ""
      }`}
      onClick={() => onClickHandle(itemIdx)}
    >
      <div />
    </S.CarrousselControl.Action>
  );
};

CarrousselAction.propTypes = {
  itemIdx: PropTypes.number.isRequired,
  activeIndex: PropTypes.number.isRequired,
  onActionClick: PropTypes.func.isRequired,
};

const CarrousselActionNavigation = ({ onActionClick, icon }) => {
  return (
    <S.CarrousselControlAction
      onClick={onActionClick}
      className="CarrousselControl Navigation"
    >
      <S.CarrousselActionNavigationIcon as={icon} />
    </S.CarrousselControlAction>
  );
};

CarrousselActionNavigation.propTypes = {
  onActionClick: PropTypes.func.isRequired,
  icon: PropTypes.elementType.isRequired,
};

const CarrousselCardItem = (props) => {
  const { children, title } = props;
  return (
    <S.Carroussel.Item
      {...props}
      as={Card}
      header={<Headline nivel="3" styleAs="7" text={title} />}
    >
      <S.CarrousselBodyText as={Text}>{children}</S.CarrousselBodyText>
    </S.Carroussel.Item>
  );
};

CarrousselCardItem.propTypes = {
  title: PropTypes.string.isRequired,
};

const CarrousselCard = (props) => {
  const { children } = props;
  const [showCard, setShowCard] = useState(1);

  const carrousselItems = useMemo(
    () =>
      React.Children.toArray(children)
        .map((el) => {
          return React.isValidElement(el) ? el : null;
        })
        .filter((x) => x),
    [children]
  );

  const showCardRange = useMemo(() => {
    return [showCard - 2, showCard - 1, showCard, showCard + 1, showCard + 2]
      .filter(
        (currentIdx) => currentIdx > 0 && currentIdx <= carrousselItems.length
      )
      .slice(-3);
  }, [carrousselItems, showCard]);

  const onNavigationClick = useCallback(
    (index) => {
      setShowCard(Math.min(carrousselItems.length, Math.max(1, index)));
    },
    [carrousselItems.length]
  );

  return (
    <S.Carroussel {...props} showCard={showCard} showCardRange={showCardRange}>
      {carrousselItems}
      <S.CarrousselControl
        carrousselItems={carrousselItems}
        className="CarrousselControl"
      >
        {carrousselItems.length > 3 && (
          <CarrousselActionNavigation
            onActionClick={() => onNavigationClick(showCard - 1)}
            icon={FaChevronLeft}
          />
        )}
        {applyKey(carrousselItems, (...[, idx]) => {
          return (
            <CarrousselAction
              activeIndex={showCard}
              itemIdx={idx + 1}
              onActionClick={setShowCard}
            />
          );
        })}
        {carrousselItems.length > 3 && (
          <CarrousselActionNavigation
            onActionClick={() => onNavigationClick(showCard + 1)}
            icon={FaChevronRight}
          />
        )}
      </S.CarrousselControl>
      <JsonData position="left" {...{ showCardRange, showCard }} disabledView />
    </S.Carroussel>
  );
};

CarrousselCard.CardItem = CarrousselCardItem;

export default CarrousselCard;
